import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CATS } from "@/components/cafe/Board";

const STATUS = { "": "Tutti", pubblicato: "Pubblicati", in_revisione: "In revisione", rifiutato: "Rifiutati" };

function AllPosts({ onChange, tick }) {
  const [status, setStatus] = useState("pubblicato");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/cafe/moderation/posts", { params: { status: status || undefined, q: q || undefined } }).then(({ data }) => setItems(data)), [status, q]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load, tick]);
  const run = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const act = (p, a, ok) => run(() => api.post(`/cafe/moderation/posts/${p.id}/${a}`), ok);
  const del = (p) => window.confirm(`Eliminare definitivamente "${p.title}"? Verranno cancellate anche segnalazioni e risposte.`) && run(() => api.delete(`/cafe/moderation/posts/${p.id}`), "Annuncio eliminato");
  return (
    <div className="space-y-3" data-testid="mod-all-posts">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Gestione annunci ({items.length})</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(STATUS).map(([k, l]) => <Button key={k || "all"} size="sm" variant={status === k ? "default" : "outline"} data-testid={`mod-filter-${k || "all"}`} onClick={() => setStatus(k)}>{l}</Button>)}
        <Input data-testid="mod-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca titolo o autore" className="h-9 max-w-xs" />
      </div>
      {!items.length && <p className="text-sm text-slate-500">Nessun annuncio.</p>}
      {items.map((p) => (
        <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`mod-item-${p.id}`}>
          <p className="text-sm font-bold">{p.title} <span className="text-xs font-normal text-slate-500">· {CATS[p.category]} · {p.author} · {STATUS[p.status] || p.status}{p.official ? " · ufficiale" : p.verified ? " · verificato" : ""}{p.hidden ? " · nascosto" : ""} · {fmtDateTime(p.created_at)}</span></p>
          <p className="mt-1 line-clamp-2 text-xs text-slate-600">{p.description}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {p.status !== "pubblicato" && <Button size="sm" variant="outline" data-testid={`mod-item-publish-${p.id}`} onClick={() => act(p, "approve", "Pubblicato")}>Pubblica</Button>}
            {p.status === "pubblicato" && <Button size="sm" variant="outline" data-testid={`mod-item-unpublish-${p.id}`} onClick={() => act(p, "unpublish", "Rimesso in revisione")}>Ritira</Button>}
            {!p.verified && <Button size="sm" variant="outline" data-testid={`mod-item-verify-${p.id}`} onClick={() => act(p, "verify", "Segnato come verificato")}>Verifica</Button>}
            <Button size="sm" variant="outline" data-testid={`mod-item-official-${p.id}`} onClick={() => act(p, p.official ? "unofficial" : "official", p.official ? "Non più ufficiale" : "Segnato come ufficiale")}>{p.official ? "Togli ufficiale" : "Rendi ufficiale"}</Button>
            <Button size="sm" variant="outline" data-testid={`mod-item-hide-${p.id}`} onClick={() => act(p, p.hidden ? "unhide" : "hide", p.hidden ? "Di nuovo visibile" : "Nascosto")}>{p.hidden ? "Mostra" : "Nascondi"}</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid={`mod-item-delete-${p.id}`} onClick={() => del(p)}><Trash2 className="mr-1 h-3.5 w-3.5" />Elimina</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function CafeModeration() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/cafe/moderation").then(({ data }) => setD(data)), []);
  useEffect(() => {
    load();
  }, [load]);
  const act = async (url, ok) => {
    try {
      await api.post(url);
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!d) return null;
  return (
    <div className="space-y-5" data-testid="cafe-moderation">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Annunci da moderare ({d.posts.length})</p>
      {!d.posts.length && <p className="text-sm text-slate-500">Nessun annuncio in attesa.</p>}
      {d.posts.map((p) => (
        <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`mod-post-${p.id}`}>
          <p className="text-sm font-bold">{p.title} <span className="text-xs font-normal text-slate-500">· {CATS[p.category]} · {p.author} · {fmtDateTime(p.created_at)}</span></p>
          <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{p.description}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" data-testid={`mod-verify-${p.id}`} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(`/cafe/moderation/posts/${p.id}/verify`, "Pubblicato come verificato")}>Pubblica verificato</Button>
            <Button size="sm" variant="outline" data-testid={`mod-approve-${p.id}`} onClick={() => act(`/cafe/moderation/posts/${p.id}/approve`, "Pubblicato come non verificato")}>Pubblica non verificato</Button>
            <Button size="sm" variant="outline" className="text-rose-700" data-testid={`mod-reject-${p.id}`} onClick={() => act(`/cafe/moderation/posts/${p.id}/reject`, "Annuncio rifiutato")}>Rifiuta</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid={`mod-delete-${p.id}`} onClick={() => window.confirm("Eliminare definitivamente questo annuncio?") && api.delete(`/cafe/moderation/posts/${p.id}`).then(() => { toast.success("Annuncio eliminato"); load(); }).catch((e) => toast.error(errMsg(e)))}><Trash2 className="mr-1 h-3.5 w-3.5" />Elimina</Button>
          </div>
        </div>
      ))}
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Segnalazioni aperte ({d.reports.length})</p>
      {!d.reports.length && <p className="text-sm text-slate-500">Nessuna segnalazione.</p>}
      {d.reports.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`mod-report-${r.id}`}>
          <span><b>{r.kind}</b> di {r.author || "?"}: “{r.content}” · {r.count} segnalazioni · {[...new Set(r.reasons)].join(", ")} · {fmtDateTime(r.at)}</span>
          <div className="flex gap-2">
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid={`mod-report-hide-${r.id}`} onClick={() => act(`/cafe/moderation/reports/${r.id}/hide`, "Contenuto nascosto")}>Nascondi contenuto</Button>
            <Button size="sm" variant="outline" data-testid={`mod-report-dismiss-${r.id}`} onClick={() => act(`/cafe/moderation/reports/${r.id}/dismiss`, "Segnalazione archiviata")}>Archivia</Button>
          </div>
        </div>
      ))}
      <AllPosts onChange={load} tick={d} />
    </div>
  );
}
